import { c as _c } from "react/compiler-runtime";
import { IconMinus, IconTrendingDown, IconTrendingUp } from "@tabler/icons-react";
import { Bar, BarChart, CartesianGrid, XAxis } from "recharts";
import React, { useMemo } from "react";
import { useTranslations } from "next-intl";
import { Badge } from "@components/ui/badge";
import { Card, CardAction, CardDescription, CardFooter, CardHeader, CardTitle } from "@components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@components/ui/chart";
import { useProposalChartData } from "./use-proposal-chart-data";
interface ProposalsStatsWidgetProps {
  proposals?: {
    createdAt?: Date | undefined;
  }[];
  isLoading?: boolean;
}
export const ProposalsCountByMonthStatsWidget = t0 => {
  const $ = _c(77);
  const {
    proposals,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const t = useTranslations("features.events-program.ui.widgets.proposalsStatsWidget");
  const tProposal = useTranslations("features.proposals.domain.proposal");
  const {
    chartData,
    metrics,
    isEmpty
  } = useProposalChartData(proposals);
  let t2;
  if ($[0] !== tProposal) {
    t2 = tProposal("label_plural");
    $[0] = tProposal;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      light: "var(--chart-3)",
      dark: "var(--chart-1)"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2) {
    t4 = {
      count: {
        label: t2,
        theme: t3
      }
    };
    $[3] = t2;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const chartConfig = t4 satisfies ChartConfig;
  if (isLoading && !proposals) {
    let t5;
    if ($[5] !== t) {
      t5 = t("subtitle");
      $[5] = t;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] !== t5) {
      t6 = <CardDescription>{t5}</CardDescription>;
      $[7] = t5;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">—</CardTitle>;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    let t8;
    if ($[10] !== t6) {
      t8 = <CardHeader>{t6}{t7}</CardHeader>;
      $[10] = t6;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    let t9;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <CardFooter className="flex flex-col items-start gap-1.5"><div className="min-h-[200px] w-full animate-pulse rounded bg-muted" /></CardFooter>;
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    let t10;
    if ($[13] !== t8) {
      t10 = <Card className="@container/card shadow-xs">{t8}{t9}</Card>;
      $[13] = t8;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    return t10;
  }
  if (isEmpty || !metrics.showChart) {
    let t5;
    if ($[15] !== t) {
      t5 = t("subtitle");
      $[15] = t;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] !== t5) {
      t6 = <CardDescription>{t5}</CardDescription>;
      $[17] = t5;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] !== metrics.totalProposals) {
      t7 = <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">{metrics.totalProposals}</CardTitle>;
      $[19] = metrics.totalProposals;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] !== metrics.totalProposals || $[22] !== t) {
      t8 = metrics.totalProposals === 0 ? t("empty.noProposals") : t("empty.insufficientData");
      $[21] = metrics.totalProposals;
      $[22] = t;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    let t9;
    if ($[24] !== t8) {
      t9 = <CardAction><Badge variant="outline" className="text-muted-foreground">{t8}</Badge></CardAction>;
      $[24] = t8;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    let t10;
    if ($[26] !== t6 || $[27] !== t7 || $[28] !== t9) {
      t10 = <CardHeader>{t6}{t7}{t9}</CardHeader>;
      $[26] = t6;
      $[27] = t7;
      $[28] = t9;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    let t11;
    if ($[30] !== t) {
      t11 = t("empty.description");
      $[30] = t;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    let t12;
    if ($[32] !== t11) {
      t12 = <CardFooter className="flex flex-col items-start gap-1.5"><div className="flex min-h-[200px] w-full items-center justify-center text-sm text-muted-foreground">{t11}</div></CardFooter>;
      $[32] = t11;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    let t13;
    if ($[34] !== t10 || $[35] !== t12) {
      t13 = <Card className="@container/card shadow-xs">{t10}{t12}</Card>;
      $[34] = t10;
      $[35] = t12;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    return t13;
  }
  let t5;
  if ($[37] !== metrics.trend) {
    t5 = () => {
      switch (metrics.trend) {
        case "increasing":
          {
            return <IconTrendingUp className="h-4 w-4" />;
          }
        case "decreasing":
          {
            return <IconTrendingDown className="h-4 w-4" />;
          }
        default:
          {
            return <IconMinus className="h-4 w-4" />;
          }
      }
    };
    $[37] = metrics.trend;
    $[38] = t5;
  } else {
    t5 = $[38];
  }
  const getTrendIcon = t5;
  let t6;
  if ($[39] !== getTrendIcon || $[40] !== metrics.trend || $[41] !== metrics.trendPercentage || $[42] !== t) {
    const getTrendDisplay = () => {
      const icon = getTrendIcon();
      const trendText = t(`trend.${metrics.trend}`);
      if (metrics.trendPercentage > 0) {
        const sign = metrics.trend === "increasing" ? "+" : "-";
        return {
          content: <>{icon}{trendText} {sign}{metrics.trendPercentage}%</>,
          variant: metrics.trend === "increasing" ? "default" : "secondary"
        };
      }
      return {
        content: <>{icon}{trendText}</>,
        variant: "outline"
      };
    };
    t6 = getTrendDisplay();
    $[39] = getTrendIcon;
    $[40] = metrics.trend;
    $[41] = metrics.trendPercentage;
    $[42] = t;
    $[43] = t6;
  } else {
    t6 = $[43];
  }
  const trendDisplay = t6;
  let t7;
  if ($[44] !== t) {
    t7 = t("subtitle");
    $[44] = t;
    $[45] = t7;
  } else {
    t7 = $[45];
  }
  let t8;
  if ($[46] !== metrics.timeRange || $[47] !== t7) {
    t8 = <CardDescription>{t7} • {metrics.timeRange}</CardDescription>;
    $[46] = metrics.timeRange;
    $[47] = t7;
    $[48] = t8;
  } else {
    t8 = $[48];
  }
  let t9;
  if ($[49] !== metrics.totalProposals) {
    t9 = <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">{metrics.totalProposals}</CardTitle>;
    $[49] = metrics.totalProposals;
    $[50] = t9;
  } else {
    t9 = $[50];
  }
  const t10 = trendDisplay.variant as "default" | "destructive" | "outline" | "secondary" | null | undefined;
  let t11;
  if ($[51] !== t10 || $[52] !== trendDisplay.content) {
    t11 = <CardAction><Badge variant={t10}>{trendDisplay.content}</Badge></CardAction>;
    $[51] = t10;
    $[52] = trendDisplay.content;
    $[53] = t11;
  } else {
    t11 = $[53];
  }
  let t12;
  if ($[54] !== t11 || $[55] !== t8 || $[56] !== t9) {
    t12 = <CardHeader>{t8}{t9}{t11}</CardHeader>;
    $[54] = t11;
    $[55] = t8;
    $[56] = t9;
    $[57] = t12;
  } else {
    t12 = $[57];
  }
  let t13;
  let t14;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <CartesianGrid vertical={false} strokeDasharray="3 3" opacity={0.3} />;
    t14 = <XAxis dataKey="name" tickLine={false} tickMargin={10} axisLine={false} className="text-xs" tickFormatter={_temp} />;
    $[58] = t13;
    $[59] = t14;
  } else {
    t13 = $[58];
    t14 = $[59];
  }
  let t15;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ChartTooltip content={<ChartTooltipContent />} labelFormatter={_temp2} />;
    $[60] = t15;
  } else {
    t15 = $[60];
  }
  let t16;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Bar dataKey="count" fill="var(--color-count)" radius={[4, 4, 0, 0]} opacity={0.9} />;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  let t17;
  if ($[62] !== chartData) {
    t17 = <BarChart accessibilityLayer={true} data={chartData}>{t13}{t14}{t15}{t16}</BarChart>;
    $[62] = chartData;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] !== chartConfig || $[65] !== t17) {
    t18 = <ChartContainer config={chartConfig} className="min-h-[200px] w-full">{t17}</ChartContainer>;
    $[64] = chartConfig;
    $[65] = t17;
    $[66] = t18;
  } else {
    t18 = $[66];
  }
  let t19;
  if ($[67] !== metrics.averagePerMonth || $[68] !== metrics.mostActiveMonth || $[69] !== t) {
    t19 = metrics.mostActiveMonth && <div className="mt-2 flex flex-wrap gap-2"><Badge variant="outline" className="text-xs">{t("insights.mostActiveMonth")}: {metrics.mostActiveMonth}</Badge>{metrics.averagePerMonth > 0 && <Badge variant="outline" className="text-xs">{t("insights.averagePerMonth")}: {metrics.averagePerMonth}</Badge>}</div>;
    $[67] = metrics.averagePerMonth;
    $[68] = metrics.mostActiveMonth;
    $[69] = t;
    $[70] = t19;
  } else {
    t19 = $[70];
  }
  let t20;
  if ($[71] !== t18 || $[72] !== t19) {
    t20 = <CardFooter className="flex flex-col items-start gap-1.5">{t18}{t19}</CardFooter>;
    $[71] = t18;
    $[72] = t19;
    $[73] = t20;
  } else {
    t20 = $[73];
  }
  let t21;
  if ($[74] !== t12 || $[75] !== t20) {
    t21 = <Card className="@container/card shadow-xs">{t12}{t20}</Card>;
    $[74] = t12;
    $[75] = t20;
    $[76] = t21;
  } else {
    t21 = $[76];
  }
  return t21;
};
function _temp(value) {
  const parts = value.split(" ");
  if (parts.length === 2) {
    return `${value} ${parts[0].slice(0, 3)}`;
  }
  return value;
}
function _temp2(value_0) {
  return `${value_0}`;
}